import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import axiosAPI from "../axiosAPI";

function buildHonorariosQuery(filter = {}) {
  const params = new URLSearchParams();
  if (filter.clientId) params.set("client_id", filter.clientId);
  if (filter.lawsuitId) params.set("lawsuit_id", filter.lawsuitId);
  if (filter.type) params.set("type", filter.type);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function normalizeHonorarios(data) {
  if (Array.isArray(data?.honorarios)) return data.honorarios;
  return [];
}

export const useHonorarios = (filter = {}) => {
  const queryClient = useQueryClient();
  const queryString = buildHonorariosQuery(filter);

  const query = useQuery({
    queryKey: ["honorarios", "list", queryString],
    queryFn: async () => {
      const { data } = await axiosAPI.get(`/honorarios${queryString}`);
      return data;
    },
  });

  const invalidateHonorarios = () => {
    queryClient.invalidateQueries({ queryKey: ["honorarios"] });
    queryClient.invalidateQueries({ queryKey: ["lawsuit-financial-summary"] });
    queryClient.invalidateQueries({ queryKey: ["financial"] });
  };

  const createMutation = useMutation({
    mutationFn: async (payload) => {
      const { data } = await axiosAPI.post("/honorarios", payload);
      return data;
    },
    onSuccess: () => {
      invalidateHonorarios();
      toast.success("Honorário cadastrado.", { position: "bottom-right" });
    },
    onError: () => {
      toast.error("Ops, ocorreu um erro ao cadastrar o honorário.");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const { data } = await axiosAPI.put(`/honorarios/${id}`, payload);
      return data;
    },
    onSuccess: () => {
      invalidateHonorarios();
      toast.success("Honorário atualizado.", { position: "bottom-right" });
    },
    onError: () => {
      toast.error("Ops, ocorreu um erro ao atualizar o honorário.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (honorarioId) => {
      const { data } = await axiosAPI.delete(`/honorarios/${honorarioId}`);
      return data;
    },
    onSuccess: () => {
      invalidateHonorarios();
      toast.success("Honorário excluído.", { position: "bottom-right" });
    },
    onError: () => {
      toast.error("Ops, ocorreu um erro ao excluir o honorário.");
    },
  });

  return {
    honorarios: normalizeHonorarios(query.data),
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.error,
    refetch: query.refetch,
    createHonorario: createMutation.mutateAsync,
    updateHonorario: updateMutation.mutateAsync,
    deleteHonorario: deleteMutation.mutateAsync,
    isCreating: createMutation.isLoading,
    isUpdating: updateMutation.isLoading,
    isDeleting: deleteMutation.isLoading,
    isMutating:
      createMutation.isLoading ||
      updateMutation.isLoading ||
      deleteMutation.isLoading,
  };
};
